import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { JJamesAlertComponent, JJamesInputComponent, JJamesCardComponent, JjamesButtonComponent } from 'shared-lib';
import { ParkingZoneService } from '../../../api/gen/api/parkingZone.service';
import { ParkingZoneDto } from '../../../api/gen/model/parkingZoneDto';
import { EditParkingZoneRequest } from '../../../api/gen/model/editParkingZoneRequest';
import { GovernanceType } from '../../../api/gen/model/governanceType';

@Component({
  selector: 'app-edit-parking-zone',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    JjamesButtonComponent,
    JJamesInputComponent,
    JJamesCardComponent,
    JJamesAlertComponent
  ],
  templateUrl: './edit-parking-zone.component.html',
  styleUrl: './edit-parking-zone.component.scss'
})
export class EditParkingZoneComponent implements OnInit {
  editZoneForm: FormGroup;
  isSubmitting = false;
  isLoading = true;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  zoneId: string | null = null;
  currentZone: ParkingZoneDto | null = null;

  governanceTypes = Object.values(GovernanceType);

  constructor(
    private fb: FormBuilder,
    private parkingZoneService: ParkingZoneService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.editZoneForm = this.createForm();
  }

  ngOnInit(): void {
    this.zoneId = this.route.snapshot.paramMap.get('zoneId');
    if (this.zoneId) {
      this.loadZone();
    } else {
      this.router.navigate(['/parking-spaces']);
    }
  }

  private createForm(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      capacity: [0, [Validators.required, Validators.min(1), Validators.max(10000)]],
      governanceType: [GovernanceType.Internal, [Validators.required]],
      address: ['', [Validators.maxLength(200)]]
    });
  }

  private loadZone(): void {
    if (!this.zoneId) return;

    this.isLoading = true;
    this.parkingZoneService.parkingZoneParkingZoneIdGet(this.zoneId).subscribe({
      next: (response: any) => {
        this.currentZone = response;
        this.populateForm(response);
        this.isLoading = false;
      },
      error: (error: any) => {
        this.errorMessage = 'Failed to load parking zone details' + error.error;
        this.isLoading = false;
        console.error('Error loading zone:', error);
      }
    });
  }

  private populateForm(zone: ParkingZoneDto): void {
    this.editZoneForm.patchValue({
      name: zone.name || '',
      description: zone.description || '',
      capacity: zone.capacity || 0,
      governanceType: zone.governanceType || GovernanceType.Internal,
      address: zone.address?.firstAddressLine || ''
    });
  }

  onSubmit(): void {
    if (this.editZoneForm.valid && this.zoneId) {
      this.isSubmitting = true;
      this.errorMessage = null;

      const formValue = this.editZoneForm.value;
      const updateRequest: EditParkingZoneRequest = {
        name: formValue.name,
        description: formValue.description,
        capacity: formValue.capacity,
        governanceType: formValue.governanceType,
        address: formValue.address ? {
          firstAddressLine: formValue.address
        } : undefined
      };

      this.parkingZoneService.parkingZoneParkingZoneIdPut(this.zoneId, updateRequest).subscribe({
        next: (response) => {

          if (response) {
            this.successMessage = 'Parking zone updated successfully!';
            this.isSubmitting = false;
            // Navigate back after a short delay to show success message
            setTimeout(() => {
              this.onCancel();
            }, 2000);
          }
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to update parking zone. Please try again.';
          this.isSubmitting = false;
          console.error('Error updating zone:', error);
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.router.navigate(['/parking-spaces']);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.editZoneForm.controls).forEach(key => {
      const control = this.editZoneForm.get(key);
      control?.markAsTouched();
    });
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string): boolean {
    const field = this.editZoneForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.editZoneForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['minlength']) return `${fieldName} must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['maxlength']) return `${fieldName} must not exceed ${field.errors['maxlength'].requiredLength} characters`;
      if (field.errors['min']) return `${fieldName} must be at least ${field.errors['min'].min}`;
      if (field.errors['max']) return `${fieldName} must not exceed ${field.errors['max'].max}`;
    }
    return '';
  }
}
